import React, { useState } from 'react';
import { Autocomplete, Box, Button, Chip, FormLabel, IconButton, Stack, TextField, Typography } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ccs from 'countrycitystatejson';
import axios from 'axios';
import { APIURL } from '../../../config';

const CHIP_COLOR = '#1976d2';

const selectableChipSx = (selected, color = CHIP_COLOR) => ({
    flex: 1,
    justifyContent: 'center',
    bgcolor: selected ? color : 'transparent',
    color: selected ? '#fff' : color,
    border: `1px solid ${color}`,
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': { bgcolor: selected ? color : `${color}1a` },
});

const COMM_MODE_OPTIONS = [
    { value: 'Email', label: 'Email' },
    { value: 'Phone', label: 'Phone' },
];

const PersonalDetailsCard = (props) => {
    const [addressSuggestions, setAddressSuggestions] = useState([]);

    const handleAddressSuggestions = async (value) => {
        const res = await axios.get(APIURL + '/maps', { params: { input: value } });
        setAddressSuggestions(res.data.predictions || []);
    };

    const handleAddressSelect = async (value) => {
        const details = await axios.get(APIURL + '/maps/details', { params: { place_id: value.place_id } });
        props.formChangeHandler({ target: { id: 'address_maps' }, value: details.data });
    };

    const code = ccs.getCountryByShort('US');
    const vals = props.formVals;
    const addr2 = (vals.address_line_2 || '').trim();
    const currAddress = vals.address_line_1 + ', ' + (addr2 ? addr2 + ', ' : '') + vals.city + ', ' + vals.state + ', ' + vals.zip;

    const sub = props.submitAttempted;
    const errFirst = sub && !vals.first_name;
    const errLast = sub && !vals.last_name;
    const errAddress = sub && !vals.address_line_1;
    const errComm = sub && !vals.prefCommMode;
    const errEmail = sub && !vals.Cemail;

    return (
        <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField
                    sx={{ flex: 1 }}
                    label="First Name"
                    value={vals.first_name}
                    onChange={props.formChangeHandler}
                    variant="outlined"
                    size="small"
                    id="first_name"
                    required
                    inputProps={{ maxLength: 50 }}
                    error={errFirst}
                    helperText={errFirst ? 'First name is required' : ''}
                />
                <TextField
                    sx={{ flex: 1 }}
                    label="Last Name"
                    value={vals.last_name}
                    onChange={props.formChangeHandler}
                    variant="outlined"
                    size="small"
                    id="last_name"
                    required
                    inputProps={{ maxLength: 50 }}
                    error={errLast}
                    helperText={errLast ? 'Last name is required' : ''}
                />
            </Box>

            <Autocomplete
                defaultValue={{ description: /^[, ]+$/.test(currAddress) ? '' : currAddress }}
                freeSolo
                autoSelect
                selectOnFocus
                disablePortal
                id="address_maps"
                fullWidth
                size="small"
                popupIcon={null}
                options={addressSuggestions}
                getOptionLabel={(option) => (option.description !== undefined ? option.description : option)}
                onChange={(event, value, reason) => {
                    if (reason === 'selectOption') handleAddressSelect(value);
                }}
                onInputChange={(event, value, reason) => {
                    if (reason === 'input') handleAddressSuggestions(value);
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Address"
                        variant="outlined"
                        required
                        error={errAddress}
                        helperText={errAddress ? 'Address is required' : ''}
                    />
                )}
            />

            <Box>
                <FormLabel component="legend" required error={errComm} sx={{ fontSize: '0.85rem', mb: 0.75, display: 'block' }}>
                    Preferred Communication Mode
                </FormLabel>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {COMM_MODE_OPTIONS.map((opt) => {
                        const selected = vals.prefCommMode === opt.value;
                        return (
                            <Chip
                                key={opt.value}
                                label={opt.label}
                                onClick={() => props.formChangeHandler({ target: { id: 'prefCommMode', value: opt.value } })}
                                sx={selectableChipSx(selected)}
                            />
                        );
                    })}
                </Box>
                {errComm && (
                    <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
                        Preferred communication mode is required
                    </Typography>
                )}
            </Box>

            <TextField
                fullWidth
                label="Email Address"
                value={vals.Cemail || ''}
                onChange={props.formChangeHandler}
                variant="outlined"
                size="small"
                id="Cemail"
                required
                inputProps={{ maxLength: 60 }}
                error={errEmail}
                helperText={errEmail ? 'Email address is required' : ''}
            />

            <Box>
                <FormLabel component="legend" sx={{ fontSize: '0.85rem', mb: 0.75, display: 'block' }}>
                    Phone Numbers
                </FormLabel>
                <Stack spacing={1.5}>
                    {props.phnNumList.map((phn, indx) => {
                        const errPhone = sub && !phn.phone;
                        return (
                            <Box key={phn.phnId} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <TextField
                                    sx={{ flex: 1 }}
                                    label="Country Code"
                                    value={`${code.emoji} ${code.name}(${code.phone})`}
                                    variant="outlined"
                                    size="small"
                                    id="country_code"
                                    disabled
                                />
                                <TextField
                                    sx={{ flex: 1.5 }}
                                    label={`Phone Line ${indx + 1}`}
                                    value={phn.phone}
                                    onChange={(event) => props.handlePhoneListChange(phn.phnId, event)}
                                    variant="outlined"
                                    size="small"
                                    id="phone"
                                    required
                                    inputProps={{ maxLength: 50 }}
                                    type="number"
                                    error={errPhone}
                                    helperText={errPhone ? 'Phone number is required' : ''}
                                />
                                {props.phnNumList.length > 1 && (
                                    <IconButton
                                        aria-label="delete phone"
                                        onClick={() => props.deletePhoneNumber(phn.phnId)}
                                        sx={{ color: '#d32f2f', mt: 0.5 }}
                                    >
                                        <DeleteOutlineOutlinedIcon />
                                    </IconButton>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
                <Button
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => props.addPhoneNumber()}
                    size="small"
                    sx={{ mt: 1, textTransform: 'none' }}
                >
                    Add Another Phone Number
                </Button>
            </Box>
        </Stack>
    );
};

export default PersonalDetailsCard;
